import { useFeatures } from '@kmx/io-core/context/FeaturesContext';
import { getCookieValue } from '@kmx/io-core/utils/cookie';
import { useCallback, useMemo, useState } from 'react';
import { startQuoteV2 } from '../../api/quote';
import { useCustomerInfo } from '../../context/customerInfo';
import { useFormContext } from '../../context/formContext';
import { useOfferContext } from '../../context/offerContext';
import { useStoreInfo } from '../../context/storeInfo';
import { useVehicleInfo } from '../../context/vehicleInfo';
import { IStartQuote } from '../../types/IStartQuote';
import { IVehicleInfo } from '../../types/IVehicleInfo';
import { IVinEligibility } from '../../types/IVinEligibility';
import { featureName } from '../../types/testNames';
import { activeStoreAppraisal, zipCodeDecodeError } from '../../utils/offerEvents';
import { goToOfferDetails } from '../../utils/redirect';
import useTriggerIneligibleEvent from './useTriggerIneligibleEvent';

interface ITriggerStartQuote {
    vehicleData: IVehicleInfo;
    pageType?: string;
}

interface IUseStartQuote {
    startQuote?: IVinEligibility;
}

export default function useStartQuote(props: IUseStartQuote) {
    const { trigger: vehicleIneligible } = useTriggerIneligibleEvent();
    const [eligibilityResponses, setEligibilityResponses] = useState<IVinEligibility>({ ...props.startQuote });
    const { setIneligibleReason, setIneligible, setQuoteId, extraQuestions, setStoreAppraisal } = useOfferContext();
    const { setStoreInfo } = useStoreInfo();
    const { setFormMetadata } = useFormContext();
    const { setCustomerInfo } = useCustomerInfo();
    const { setVehicleConditionInfo, setFeatureLookupInfo } = useVehicleInfo();

    const { initializeFeatures, isFeatureEnabled } = useFeatures();

    const start = useCallback(
        async ({ vehicleData, pageType }: ITriggerStartQuote): Promise<IStartQuote | null> => {
            if (eligibilityResponses[vehicleData.vin] && eligibilityResponses[vehicleData.vin].startQuote?.isEligible) {
                return eligibilityResponses[vehicleData.vin];
            }

            const features = await initializeFeatures();
            const startQuoteResponse = await startQuoteV2(
                vehicleData.vin,
                vehicleData.plate,
                vehicleData.state,
                getCookieValue('KmxMyKmx_0', 'userid'),
                vehicleData.zipcode,
                extraQuestions,
                features.join()
            );
            setEligibilityResponses({ ...eligibilityResponses, vin: startQuoteResponse });

            if (window?.FS?.setUserVars && startQuoteResponse?.startQuote?.quoteId) {
                window.FS.setUserVars({
                    vin: vehicleData.vin,
                    quoteId: startQuoteResponse.startQuote.quoteId,
                });
            }

            if (
                startQuoteResponse &&
                startQuoteResponse.store &&
                startQuoteResponse.isSuccess &&
                startQuoteResponse.startQuote?.isEligible
            ) {
                setQuoteId(startQuoteResponse.startQuote.quoteId);
                setVehicleConditionInfo(prev => ({
                    ...prev,
                    conditionQuestions: startQuoteResponse.conditionQuestions,
                }));
                if (startQuoteResponse?.customerProfile) {
                    setCustomerInfo({
                        ciamId: startQuoteResponse.customerProfile.ciamId,
                        email: startQuoteResponse.customerProfile.email,
                    });
                }
                setStoreInfo(startQuoteResponse.store);
                setFeatureLookupInfo(startQuoteResponse.skus);
            } else if (!startQuoteResponse.store) {
                zipCodeDecodeError(vehicleData.zipcode, startQuoteResponse.faults[0] || null);
            } else if (startQuoteResponse.activeStoreAppraisal) {
                setStoreAppraisal(startQuoteResponse.activeStoreAppraisal);
                activeStoreAppraisal(true, vehicleData, startQuoteResponse.activeStoreAppraisal);
            } else if (
                isFeatureEnabled(featureName.OFFER_DETAILS_ON_SUBMIT, features) &&
                startQuoteResponse?.startQuote?.isEligible === false &&
                (startQuoteResponse?.startQuote?.reason === 'pending-pbr' ||
                    startQuoteResponse?.startQuote?.reason === 'active-pbr') &&
                startQuoteResponse?.startQuote?.details?.extensions?.parentOfferId
            ) {
                const parentOfferId = startQuoteResponse?.startQuote?.details?.extensions?.parentOfferId;

                goToOfferDetails(parentOfferId);

                return null;
            } else {
                // Check if we have created a quote.
                if (startQuoteResponse && startQuoteResponse.startQuote && startQuoteResponse.startQuote.quoteId) {
                    setQuoteId(startQuoteResponse.startQuote.quoteId);
                }

                setIneligibleReason(startQuoteResponse?.startQuote?.reason);
                setIneligible(true);

                // ymm-ineligible or VIN eligibility
                vehicleIneligible({
                    reason: startQuoteResponse?.startQuote?.reason,
                    pageType,
                });
            }

            return startQuoteResponse;
        },
        [
            eligibilityResponses,
            extraQuestions,
            initializeFeatures,
            isFeatureEnabled,
            setCustomerInfo,
            setFeatureLookupInfo,
            setFormMetadata,
            setIneligible,
            setIneligibleReason,
            setQuoteId,
            setStoreAppraisal,
            setStoreInfo,
            setVehicleConditionInfo,
            vehicleIneligible,
        ]
    );

    return useMemo(() => ({ start }), [start]);
}
